import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import ButtonSolid from './ButtonSolid';

export const ASSET_TYPES = [
    'Parts Pages',
    'Manuals',
    'Electrical Schematics',
    'Pneumatic Schematics',
    'Tutorial Videos',
    'Filler Controller Manuals',
    'Control Package',
];

export const CONTROL_PACKAGE_TYPE = 'Control Package';

// Control Package assets are generated from their assembly, never uploaded by hand.
export const UPLOAD_ASSET_TYPES = ASSET_TYPES.filter((type) => type !== CONTROL_PACKAGE_TYPE);

const VIDEO_EXTENSIONS = ['mov', 'mp4'];
const DOCUMENT_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];

const getFileExtension = (filename) => filename.split('.').pop().toLowerCase();

const isValidFileForType = (file, type) => {
    const ext = getFileExtension(file.name);
    if (type === 'Tutorial Videos') {
        return VIDEO_EXTENSIONS.includes(ext);
    }
    return DOCUMENT_EXTENSIONS.includes(ext);
};

function AssetDropzone({ disabled, onFileSelected, defaultType, lockedType, continueLabel, allowedTypes = UPLOAD_ASSET_TYPES }) {
    const [isDragging, setIsDragging] = useState(false);
    const [assetType, setAssetType] = useState(lockedType || (allowedTypes.includes(defaultType) ? defaultType : ''));
    const [selectedFile, setSelectedFile] = useState(null);
    const inputRef = useRef(null);

    const noTypeSelected = !assetType;
    const dropzoneDisabled = disabled || noTypeSelected;

    const acceptForType = assetType === 'Tutorial Videos'
        ? 'video/mp4,video/quicktime,.mov,.mp4'
        : 'image/*,application/pdf';

    const handleFile = (file) => {
        if (!isValidFileForType(file, assetType)) {
            const supported = assetType === 'Tutorial Videos'
                ? 'MP4, MOV'
                : 'PDF, JPG, PNG, GIF, BMP, WEBP';
            toast.error(`Unsupported file "${file.name}". Supported formats for ${assetType}: ${supported}.`);
            return;
        }
        setSelectedFile(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (dropzoneDisabled) return;
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
        e.target.value = '';
    };

    const handleContinue = () => {
        if (!selectedFile || !assetType) return;
        onFileSelected(selectedFile, assetType);
    };

    return (
        <div className='mt-4'>
            {lockedType ? (
                <p className='text-gray-700 font-medium mb-2'>Asset Type: <span className='font-semibold'>{lockedType}</span></p>
            ) : (
                <label className='block text-gray-700 font-medium mb-2'>
                    Asset Type
                    <select
                        value={assetType}
                        onChange={(e) => { setAssetType(e.target.value); setSelectedFile(null); }}
                        disabled={disabled}
                        className='block appearance-none w-full h-10 md:h-12 rounded-md border border-gray-300 focus:outline-gray-400 py-2 px-3 md:px-4 text-gray-900 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed mt-1'
                    >
                        <option value=''>Select Type</option>
                        {allowedTypes.map((type) => (
                            <option key={type} value={type}>{type}</option>
                        ))}
                    </select>
                </label>
            )}
            <div
                onDragOver={(e) => { e.preventDefault(); if (!dropzoneDisabled) setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={() => { if (!dropzoneDisabled) inputRef.current?.click(); }}
                className={`flex flex-col items-center justify-center border-2 border-dashed rounded-md p-6 text-center transition-colors ${dropzoneDisabled
                    ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                    : isDragging
                        ? 'border-orange-400 bg-orange-50 cursor-pointer'
                        : 'border-gray-300 hover:border-gray-400 cursor-pointer'
                    }`}
            >
                {selectedFile ? (
                    <>
                        <p className='text-sm text-gray-700 font-medium'>Selected: {selectedFile.name}</p>
                        <p className='text-xs text-gray-400 mt-1'>Drop or click to choose a different file</p>
                    </>
                ) : (
                    <>
                        <p className='text-sm text-gray-500'>
                            {disabled
                                ? 'Loading...'
                                : noTypeSelected
                                    ? 'Select an asset type first'
                                    : 'Drop a file here or click to browse'}
                        </p>
                        <p className='text-xs text-gray-400 mt-1'>
                            {noTypeSelected
                                ? ''
                                : assetType === 'Tutorial Videos'
                                    ? 'Video files only (MP4, MOV)'
                                    : 'PDF or image files only'}
                        </p>
                    </>
                )}
                <input
                    ref={inputRef}
                    type='file'
                    accept={acceptForType}
                    onChange={handleFileSelect}
                    disabled={dropzoneDisabled}
                    className='hidden'
                />
            </div>
            <ButtonSolid
                buttonText={continueLabel || 'Continue to Add'}
                disabled={disabled || !selectedFile || !assetType}
                onClick={handleContinue}
                className='w-full mt-4'
            />
        </div>
    );
}

export default AssetDropzone;
